import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Card, CardContent, CardTitle, FunnelStrip } from '@xxtract/ui';
import { aggregateKpiDataState, formatPercent, formatWorkdays, hasNoProcessData } from '../format';
import { SERVICE_CODE } from '../processes.constants';
import type { DienstProcessName, ProcessSummary } from '../processes.api';

/**
 * De dekkingsbadge uit de mockup: gevoed · geen data · bron offline. Vervangt de tegel
 * "{covered} van {total} stappen gedekt" op de kaart — die hoort op het dekkingsscherm, waar de
 * stappen er ook bij staan.
 */
function CoverageBadge({ process, sourceUnavailable }: { process: ProcessSummary; sourceUnavailable: boolean }) {
  const { t } = useTranslation();
  const state = sourceUnavailable ? 'offline' : hasNoProcessData(process.coverageState) ? 'noData' : 'fed';
  const tone =
    state === 'fed'
      ? 'border-[var(--success-dark)] bg-[var(--success-light)] text-[var(--success-dark)]'
      : state === 'offline'
        ? 'border-[var(--destructive)] bg-[var(--destructive-light)] text-[var(--destructive)]'
        : 'border-[var(--border)] bg-[var(--background-muted)] text-[var(--foreground-muted)]';

  return (
    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone}`} data-testid={`process-card-badge-${process.name}`}>
      {t(`processes.card.badge.${state}`)}
    </span>
  );
}

/**
 * De twee cijfers onder de funnel, zoals de mockup ze zet: gemiddelde doorlooptijd en het aandeel
 * binnen de KPI. De stilstand per dienst staat op het detailscherm, niet meer op de kaart.
 */
function LeadTimeFigures({ process }: { process: ProcessSummary }) {
  const { t } = useTranslation();
  const { kpiSummary } = process;

  // De orders-tak levert ook een doorlooptijd voor een proces zónder procesdata (measure: 106 orders),
  // maar een getal onder "Nog geen procesdata" spreekt zichzelf tegen — dus onderdrukken.
  if (hasNoProcessData(process.coverageState) || kpiSummary.total === 0) {
    return (
      <div className="border-t border-[var(--border)] pt-2.5" data-testid={`kpi-doorlooptijd-${process.name}`}>
        <p className="text-sm font-semibold text-[var(--foreground-muted)]">{t('processes.placeholder.notYetAvailable')}</p>
        <p className="text-[11px] text-[var(--foreground-muted)]">{t('processes.card.leadTime')}</p>
      </div>
    );
  }

  return (
    <div className="flex gap-5 border-t border-[var(--border)] pt-2.5" data-kpi-state={aggregateKpiDataState(kpiSummary) ?? undefined} data-testid={`kpi-doorlooptijd-${process.name}`}>
      <div>
        <p className="text-sm font-semibold text-[var(--foreground)]">
          {kpiSummary.meanWorkdays != null ? formatWorkdays(kpiSummary.meanWorkdays) : t('common.notAvailable')}
        </p>
        <p className="text-[11px] text-[var(--foreground-muted)]">{t('processes.card.leadTime')}</p>
      </div>
      {kpiSummary.pctWithinKpi != null ? (
        <div>
          <p className={`text-sm font-semibold ${kpiSummary.pctWithinKpi < 70 ? 'text-[var(--destructive)]' : 'text-[var(--foreground)]'}`}>
            {formatPercent(kpiSummary.pctWithinKpi)}
          </p>
          <p className="text-[11px] text-[var(--foreground-muted)]">{t('processes.card.kpiWithinTarget')}</p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * De dienstkaart zoals `mockup-processen-portaal.html` hem tekent: naam met dienstcode, een
 * dekkingsbadge rechtsboven, het aantal open producten groot, een tekstloze funnel-strip, de twee
 * doorlooptijd-cijfers en een pijl. De hele kaart is één link.
 */
export function ProcessCard({ process, sourceUnavailable }: { process: ProcessSummary; sourceUnavailable: boolean }) {
  const { t } = useTranslation();
  const processLabel = t(`processes.names.${process.name}`);
  // Leeg = geen funnel om te tonen: het proces bestaat nog niet in de bron ('unmapped') of het bestaat
  // wel maar heeft nog geen events ('no_data'). Beide krijgen dezelfde boodschap.
  const isEmpty = hasNoProcessData(process.coverageState);

  return (
    <Link
      className="group block rounded-[var(--radius-card)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--secondary)]"
      data-testid={`process-card-link-${process.name}`}
      // De gekozen filter overleeft de doorklik via de gedeelde lijststand `processen` (story 12).
      to={`/processes/${process.name}`}
    >
      <Card className="h-full transition-shadow group-hover:border-[var(--secondary)] group-hover:shadow-md" data-process-name={process.name} data-testid={`process-card-${process.name}`}>
        <CardContent className="flex h-full flex-col gap-3 pt-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-sm">{processLabel}</CardTitle>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--foreground-muted)]">{SERVICE_CODE[process.name as DienstProcessName] ?? process.name}</p>
            </div>
            <CoverageBadge process={process} sourceUnavailable={sourceUnavailable} />
          </div>

          <div className="flex items-baseline gap-2">
            <span className={`text-2xl font-bold tracking-tight ${isEmpty ? 'text-[var(--foreground-muted)]' : 'text-[var(--foreground)]'}`} data-testid={`process-card-open-${process.name}`}>
              {process.openProductCount}
            </span>
            <span className="text-xs text-[var(--foreground-muted)]">{t('processes.card.openProductsLabel')}</span>
          </div>

          {isEmpty ? (
            <p className="text-xs text-[var(--foreground-muted)]" data-testid={`process-card-no-data-${process.name}`}>{t('processes.noData.title')}</p>
          ) : (
            // spec-processen-actuele-stand (CAP-5): the step counts come from running orders and follow the filter.
            <FunnelStrip
              ariaLabel={t('processes.card.funnelLabel', { process: processLabel })}
              describeStep={(step) => t('processes.detail.stepCount', { count: step.count })}
              steps={process.funnel.map((step) => ({ label: step.canonicalStep, count: step.count }))}
            />
          )}

          {/* Story 7 (CAP-10): niet alle orders van deze dienst zijn opgehaald. */}
          {process.ordersTruncated ? (
            <p className="text-[11px] text-[var(--info-text)]" data-testid={`process-card-orders-truncated-${process.name}`}>{t('processes.ordersTruncated.dienst')}</p>
          ) : null}

          {/* De badge draagt dit visueel, maar wordt niet voorgelezen: de melding blijft staan zodat
              een schermlezer ook hoort dat deze cijfers oud kunnen zijn. */}
          {sourceUnavailable ? (
            <p className="text-[11px] text-[var(--info-text)]" role="status">{t('processes.degraded.title')}</p>
          ) : null}

          <LeadTimeFigures process={process} />

          <p className="mt-auto self-end text-[11px] font-semibold text-[var(--secondary)]">{t('processes.card.viewEvents')}</p>
        </CardContent>
      </Card>
    </Link>
  );
}
